import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { getErrorToastMessage } from '@/libs/api'
import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type {
  HookParams,
  UpdateProfilePicPayload,
  UploadListData,
  UploadListParams,
  UploadPayload,
} from '@/libs/interfaces'
import { uploadsApi } from '@/services/api'

// skipErrorRedirect: a missing upload/thumbnail (e.g. no profile picture set
// yet) is expected and should degrade to the caller's fallback UI (see
// EntityThumbnail), not trip the app-wide "something went wrong" redirect.
export function useUploads({ params, enabled }: HookParams<UploadListParams>) {
  return useQuery<UploadListData>({
    queryKey: QUERY_KEYS.uploads.list(params),
    queryFn: () => uploadsApi.list(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['1Hour'],
    meta: { skipErrorRedirect: true },
  })
}

export function useUploadPreSignedUrl({
  params,
  enabled,
}: HookParams<{ orgID: string; uploadID: string }>) {
  return useQuery<string>({
    queryKey: QUERY_KEYS.uploads.preSignedUrl(params.uploadID),
    queryFn: () => uploadsApi.getPreSignedUrl(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['1Hour'],
    meta: { skipErrorRedirect: true },
  })
}

// Resolves the current user's profile-pic upload into a displayable URL: look up their
// most recent profilePic-flagged upload, then presign it. Shared between AppTopbar (where
// this was already working) and the Account page's Profile tab (where it wasn't wired up
// at all — it only showed the in-session upload preview, never anything persisted).
export function useCurrentUserProfilePicUrl({ orgID, userID }: { orgID: string; userID: string }) {
  const { data: uploadsData } = useUploads({
    params: { orgID, userID, profilePic: true, limit: 1 },
    enabled: Boolean(orgID) && Boolean(userID),
  })
  const profileUploadID = uploadsData?.uploads?.[0]?.uploadID ?? ''

  return useUploadPreSignedUrl({
    params: { orgID, uploadID: profileUploadID },
    enabled: Boolean(profileUploadID),
  })
}

// Uploads are a multipart-form "create" with no dedicated create page and no
// success toast anywhere today (EntityThumbnail, Account.tsx's avatar
// picker, and the service/product/vehicle "new" forms all treat a successful
// upload as silent) — left as-is per the ambiguous-case rule, only onError
// is wrapped with the backend-localized message.
export function useUploadFile() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (payload: UploadPayload) => uploadsApi.upload(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.uploads.all })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useUploadDelete() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (params: { orgID: string; uploadID: string }) => uploadsApi.delete(params),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.uploads.all })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useUploadUpdateProfilePic() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (payload: UpdateProfilePicPayload) => uploadsApi.updateProfilePic(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.uploads.all })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}
